import {
  Dialog,
  DialogBody,
  DialogContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SkeletonLoader,
} from '@civilgrid/ui/components';
import { getAvatarLetters, getNonNullUserName } from '@civilgrid/ui/utils';
import { Link, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import authorizations, { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { logOut as apilogOut } from '../../../api/auth2';
import authStore from '../../../authStore';
import rootStore from '../../../store';
import UserProfileContent from '../UserProfile/UserProfileContent';

export default function UserSettings() {
  const navigate = useNavigate();
  const { me, currentOrganization } = useSnapshot(rootStore) as typeof rootStore;

  const [showProfileModal, setShowProfileModal] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const onCompletion = () => {
    setShowProfileModal(false);
  };

  if (!me || !currentOrganization) {
    return <SkeletonLoader width={24} height={24} className="rounded-full" />;
  }

  const name = getNonNullUserName(me.name);

  const logOut = async () => {
    const email = me.email;
    try {
      const response = await apilogOut({ email });
      authStore.reset();
      if (response.url) {
        window.location.href = response.url;
        return;
      }
    } catch {
      authStore.reset();
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label="User menu"
            data-testid="top-nav-user-menu-trigger"
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[6px] focus-ring"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold uppercase text-white">
              {getAvatarLetters(me)}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent data-testid="top-nav-user-menu">
          <DropdownMenuItem
            aria-label={`Edit profile, ${name || me.email}`}
            data-testid="top-nav-user-menu-profile"
            onSelect={() => setShowProfileModal(true)}
          >
            <div>
              {name && <div>{name}</div>}
              <div className="text-xs">{me.email}</div>
            </div>
          </DropdownMenuItem>
          {me.isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild aria-label="Admin Tools, internal only" data-testid="top-nav-user-menu-admin">
                <Link to="/admin">Admin Tools (Internal Only)</Link>
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            aria-label="Support, opens in a new tab"
            data-testid="top-nav-user-menu-support"
            onSelect={() => {
              capturePosthogEvent(POSTHOG_EVENTS.supportPageOpened, {});
              window.open(
                'https://portal.usepylon.com/civilgrid/login?kbUrl=https%3A%2F%2Fcivilgrid.help.usepylon.com%2F',
                '_blank',
              );
            }}
          >
            Support
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <Can action={Action.ViewOrgSettings}>
            <DropdownMenuItem
              aria-label="Organization Settings"
              data-testid="top-nav-user-menu-org-settings"
              disabled={!authorizations.can(Action.ViewOrgSettings)}
              onSelect={() => navigate({ to: '/orgSettings/$tabId', params: { tabId: 'general' } })}
            >
              Organization Settings
            </DropdownMenuItem>
          </Can>
          <DropdownMenuSeparator />
          <DropdownMenuItem aria-label="Logout" data-testid="top-nav-user-menu-logout" onSelect={logOut}>
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent
          title="User Profile"
          titleProps={{ className: 'text-xl font-normal' }}
          headerProps={{ className: 'px-8 pt-8 pb-0' }}
          preventDismiss
          className="z-[110] max-h-[min(650px,80vh)] w-[400px] max-w-[min(400px,calc(100vw-2rem))] rounded-t-[4px] font-sans text-xs shadow-[0_12px_40px_2px_rgba(0,0,0,0.4)]"
          overlayClassName="z-[110]"
          data-testid="top-nav-user-profile-dialog"
          // The menu item that opened the dialog is gone by close, so focus returns to the trigger instead.
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            triggerRef.current?.focus();
          }}
        >
          <DialogBody className="px-8 pt-4 pb-8">
            <UserProfileContent placement="profile" onCompletion={onCompletion} />
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}
